import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import Button from 'atomic/button';
import Icon from 'atomic/icon';
import Circle from 'atomic/circle/circle';
import { useDispatch, useSelector } from '@ecommerce/nest-ssr/common';
import OfferInterface from 'interfaces/offer.interface';
import { OffersActions, OffersState } from '../../../../../../../../reducers/offers';
import offersPrepare from 'helpers/offers-prepare';
import { ProductInterface } from 'modules/product/interfaces/product.interface';
import getAvailableFilters from 'modules/product/views/components/product-offers-header/helpers/get-available-filters';
// eslint-disable-next-line max-len
import ProductOffersFiltersMenuDesktop from 'modules/product/views/components/product-offers-header/product-offers-header-desktop/components/product-offers-filters-desktop/components/product-offers-filters-menu-desktop';
import './product-offers-filters-desktop.scss';

const ProductOffersFiltersDesktop: React.FC = () => {
    const [isFiltersMenuOpen, setIsFiltersMenuOpen] = useState(false);
    const dispatch = useDispatch();
    const activeFilters = useSelector((state: { offers: OffersState; }) => state.offers.activeFilters || []);
    const selectedFilters = useSelector((state: { offers: OffersState; }) => state.offers.selectedFilters || []);
    const offers = useSelector<{ product: ProductInterface; }, OfferInterface[]>(state => [...offersPrepare(state.product.offers.items)]);
    const filterOptionsList = getAvailableFilters(offers);
    const [menuHeight, setMenuHeight] = useState(0);
    const ref = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (ref.current) {
            setMenuHeight(ref.current.children[0].clientHeight);
        }
    }, [ref]);

    const filterOffers = (activeFilters: string[]): void => {
        dispatch({ type: OffersActions.Filter, payload: { activeFilters, replaceState: true, items: offers } });
    };

    const selectFilters = (selectedFilters: string[]): void => {
        dispatch({ type: OffersActions.SelectFilters, payload: { selectedFilters } });
    };

    const onPickMainFilters = (label: string, value: string): void => {
        const isSelected = selectedFilters.includes(value);

        if (!isSelected) {
            selectFilters([...selectedFilters, value]);
        }

        if (!isSelected && !activeFilters.includes(value)) {
            filterOffers([...activeFilters, value]);
        }

        if (isSelected) {
            selectFilters(selectedFilters.filter(item => item !== value));
        }

        if (isSelected && activeFilters.includes(value)) {
            filterOffers(activeFilters.filter(item => item !== value));
        }
    };

    const onToggleFiltersMenu = (): void => {
        setIsFiltersMenuOpen(state => {
            if (state) {
                selectFilters(activeFilters);
            }

            return !state;
        });
    };

    const onSubmitFilters = (filters: string[]): void => {
        filterOffers(filters);
        selectFilters(filters);
        setIsFiltersMenuOpen(false);
    };

    const onChangeFilters = (filters: string[]): void => {
        selectFilters(filters);
    };

    return (
        <>
            <div className="product-offers-filters">
                <div className="product-offers-filters__button">
                    <Button
                        color="grey"
                        border={true}
                        onClick={onToggleFiltersMenu}
                    >
                        <Icon size={24} name="filters" color="secondary" />
                        Wszystkie filtry
                        {selectedFilters.length > 0 && (
                            <Circle
                                className="product-offers-filters__total-checked"
                                tag="span"
                                color="white"
                                size={20}
                                border="primary"
                                hoverEffect={false}
                                background="primary"
                            >
                                {selectedFilters.length}
                            </Circle>
                        )}
                    </Button>
                </div>
                { filterOptionsList.filter(item => item.showInline && !item.isDisabled).map(item => (
                    <div className="product-offers-filters__button" key={item.value}>
                        <Button
                            className={classNames({
                                'product-offers-filters__button--active': selectedFilters.includes(item.value) && !item.isDisabled
                            })}
                            color="grey"
                            disabled={item.isDisabled}
                            border={true}
                            onClick={(): void => onPickMainFilters(item.label, item.value)}
                        >
                            <Icon name={item.icon} color="primary" size={16} />
                            {item.label}
                        </Button>
                    </div>
                ))}
            </div>
            <div
                className="product-offers-filters__menu-wrapper"
                ref={ref}
                style={{ height: isFiltersMenuOpen ? menuHeight : 0 }}
            >
                <ProductOffersFiltersMenuDesktop
                    className={classNames('product-offers-filters__menu', {
                        'product-offers-filters__menu--hidden': !isFiltersMenuOpen
                    })}
                    filters={filterOptionsList}
                    activeFilters={selectedFilters}
                    onSubmit={onSubmitFilters}
                    onChange={onChangeFilters}
                    offers={offers}
                />
            </div>
        </>
    );
};

export default ProductOffersFiltersDesktop;
